import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ShieldIcon from '@mui/icons-material/Shield';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';
import { Link } from 'react-router-dom';

const HipaaServices: React.FC = () => {
  const implementationProcess = [
    {
      title: "Gap Analysis",
      description: "We assess your current practices against HIPAA requirements to identify vulnerabilities and compliance gaps.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Risk Assessment",
      description: "We conduct a comprehensive HIPAA-required risk assessment to identify, analyze, and document potential risks to PHI.",
      icon: <PrivacyTipIcon fontSize="large" />
    },
    {
      title: "Policy Development",
      description: "We develop customized policies and procedures that align with HIPAA requirements and your organizational needs.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Safeguards Implementation",
      description: "We help you implement appropriate administrative, physical, and technical safeguards to protect PHI.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We assist in creating and maintaining required HIPAA documentation and records.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We provide comprehensive HIPAA training for your workforce to ensure understanding of compliance requirements.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Compliance Monitoring",
      description: "We establish systems for ongoing monitoring of HIPAA compliance and incident detection.",
      icon: <MonitorHeartIcon fontSize="large" />
    },
    {
      title: "Audit Preparation",
      description: "We help you prepare for potential HIPAA audits with mock audits and remediation planning.",
      icon: <BuildIcon fontSize="large" />
    }
  ];

  const keyComponents = [
    {
      title: "Privacy Rule Compliance",
      description: "Implementation of standards for the use and disclosure of Protected Health Information (PHI).",
      icon: <PrivacyTipIcon fontSize="large" />
    },
    {
      title: "Security Rule Compliance",
      description: "Administrative, physical, and technical safeguards to protect electronic PHI (ePHI).",
      icon: <ShieldIcon fontSize="large" />
    },
    {
      title: "Breach Notification",
      description: "Processes for assessment and notification requirements following a potential breach of PHI.",
      icon: <HealthAndSafetyIcon fontSize="large" />
    },
    {
      title: "Business Associate Management",
      description: "Proper management of business associate relationships with appropriate agreements (BAAs).",
      icon: <DataThresholdingIcon fontSize="large" />
    },
    {
      title: "Patient Rights",
      description: "Systems to ensure patient rights regarding access to and control of their health information.",
      icon: <SettingsSuggestIcon fontSize="large" />
    },
    {
      title: "Incident Management",
      description: "Protocols for identifying, responding to, documenting, and mitigating security incidents.",
      icon: <MonitorHeartIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Protection of sensitive patient information from unauthorized access and breaches",
    "Compliance with federal healthcare regulations and reduced risk of penalties",
    "Structured approach to identifying and managing risks to protected health information",
    "Improved patient trust and confidence in your healthcare organization",
    "Enhanced security measures against healthcare-specific cyber threats",
    "Reduced probability and impact of data breaches and security incidents",
    "Competitive advantage in healthcare partnerships and contracts",
    "Development of a privacy-aware organizational culture",
    "Cost savings through breach prevention and improved operational efficiency",
    "Better coordination with other security frameworks such as ISO 27001"
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              HIPAA Compliance Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive solutions to help healthcare organizations achieve and maintain HIPAA compliance
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is HIPAA?
              </Typography>
              <Typography paragraph>
                The Health Insurance Portability and Accountability Act (HIPAA) is a federal law enacted in 1996 
                that establishes standards for the privacy and security of protected health information (PHI). 
                HIPAA applies to covered entities (healthcare providers, health plans, and healthcare clearinghouses) 
                and their business associates.
              </Typography>
              <Typography paragraph>
                HIPAA consists of several components, including the Privacy Rule, which regulates the use and 
                disclosure of PHI; the Security Rule, which sets standards for protecting electronic PHI; and 
                the Breach Notification Rule, which requires notification of affected individuals following a 
                breach of unsecured PHI.
              </Typography>
              <Typography paragraph>
                Compliance with HIPAA is not only a legal requirement but also demonstrates your organization's 
                commitment to protecting sensitive patient information, building trust, and maintaining the 
                integrity of healthcare data.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Healthcare Data Security"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Key Components Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Key Components of HIPAA Compliance
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 4
          }}>
            {keyComponents.map((component, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: '12px',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    },
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ color: '#1976d2', mb: 2 }}>
                    {component.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {component.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {component.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* HIPAA Implementation Process */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Our HIPAA Implementation Process
          </Typography>
          <Typography 
            variant="body1" 
            textAlign="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            We follow a systematic approach to help your organization achieve and maintain HIPAA compliance, tailored to your specific needs.
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 4
        }}>
          {implementationProcess.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  height: '100%',
                  borderRadius: '12px',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2
                }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: '#1976d2', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    mr: 2
                  }}>
                    {index + 1}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {step.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {step.description}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Box sx={{ flex: '1 1 40%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Benefits of HIPAA Compliance
                </Typography>
                <Typography paragraph>
                  Beyond meeting regulatory requirements, implementing HIPAA compliance brings 
                  numerous advantages to healthcare organizations of all sizes.
                </Typography>
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Healthcare Technology"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    display: { xs: 'none', lg: 'block' }
                  }}
                />
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 50%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <List>
                  {benefits.map((benefit, index) => (
                    <ListItem 
                      key={index}
                      alignItems="flex-start"
                      sx={{ 
                        p: 0, 
                        mb: 3,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(8px)',
                        } 
                      }}
                    >
                      <ListItemIcon sx={{ mt: 0.5, minWidth: 36 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#1976d2' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit} 
                        primaryTypographyProps={{ 
                          fontSize: '1rem',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" color="#0c2340">
            Ready to Achieve HIPAA Compliance?
          </Typography>
          <Typography paragraph sx={{ maxWidth: '700px', mx: 'auto', mb: 4 }}>
            Contact our HIPAA compliance experts today to discuss how we can help your organization protect patient information and meet regulatory requirements.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            component={Link}
            to="/contact"
            sx={{ 
              py: 1.5, 
              px: 4, 
              borderRadius: '8px',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 15px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Get Started
          </Button>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HipaaServices; 